import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { indexClassPagination, getSchoolYear, getIstQuarter, getIIndQuarter, getIIIrdQuarter, getIVthQuarter, getConfiguredSchedules } from '../../Api';
import { periodoAtualPorData, corDoDiario } from '../../utils/diaryStatus';
import {
    Container,
    List,
    Emp,
    EmpMain,
    ClassIcon,
    Span,
    NameWrap,
    NoScheduleBadge,
    DiaryBadge,
    DiaryInfo,
    DiaryPeriods,
    DiaryLegend,
    Search,
    DivNewEmp,
    FormFilter,
    FormSearch,
    AreaEmp,
    InputEmp,
    Select,
    AddBtn,
    ContainerDivs,
    SkeletonRow,
    LoadMoreText,
    EmptyState,
} from './style';

const LIMIT = 15;

// "1º", "2º", "3º" → "1º, 2º e 3º"
const juntarPeriodos = (nums) =>
    nums.length <= 1 ? (nums[0] || '') : `${nums.slice(0, -1).join(', ')} e ${nums[nums.length - 1]}`;

const Cla$$ = () => {

    const navigate = useNavigate();
    const currentYear = String(new Date().getFullYear());

    const [classes, setClasses] = useState([]);
    const [years, setYears] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    const [positionAtEducationDepartment, setPositionAtEducationDepartment] = useState('');
    const [busca, setBusca] = useState("");
    const [debouncedBusca, setDebouncedBusca] = useState("");
    const [filter, setFilter] = useState("");
    const [schoolYear, setSchoolYear] = useState("");
    const [assessmentRegime, setAssessmentRegime] = useState('');
    const [periodoAtual, setPeriodoAtual] = useState(1);
    // horário da turma: id -> true (tem) / false (não tem); ausente = ainda não verificado
    const [comHorario, setComHorario] = useState({});

    const [loading, setLoading] = useState(true);       // skeleton cheio: SÓ na 1ª carga
    const [listLoading, setListLoading] = useState(false); // refetch (filtro/busca)
    const [loadingMore, setLoadingMore] = useState(false); // scroll infinito

    const idSchool = JSON.parse(sessionStorage.getItem("id-school"));

    const initializedRef = useRef(false); // já fez a primeira carga?
    const fetchingRef = useRef(false);    // trava de concorrência
    const sentinelRef = useRef(null);     // elemento observado no fim da lista

    // 🔹 Debounce da busca
    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 400);
        return () => clearTimeout(t);
    }, [busca]);

    // 🔹 Busca paginada
    const fetchClasses = useCallback(async (pageToLoad, reset) => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        try {
            const res = await indexClassPagination({
                idSchool,
                page: pageToLoad,
                limit: LIMIT,
                year: filter,
                search: debouncedBusca
            });

            const { data, hasMore: more, total: t, years: yrs } = res.data;

            setTotal(t ?? 0);
            setHasMore(Boolean(more));

            if (yrs) {
                const merged = Array.from(
                    new Set([schoolYear, ...yrs.map(String)].filter(Boolean))
                ).sort((a, b) => Number(b) - Number(a));
                setYears(merged);
            }

            setClasses(prev => (reset ? data : [...prev, ...data]));
        } finally {
            fetchingRef.current = false;
        }
    }, [idSchool, filter, schoolYear, debouncedBusca]);

    // 🔹 Setup inicial
    useEffect(() => {
        (async () => {
            const sy = await getSchoolYear(idSchool);
            const syValue = String(sy.data.data);

            const savedFilter = sessionStorage.getItem("classYearFilter");

            setSchoolYear(syValue);
            setFilter(savedFilter || syValue);

            const pos = localStorage.getItem("positionAtEducationDepartment");
            setPositionAtEducationDepartment(pos);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 🔹 Recarrega da página 1 quando ano/busca mudam
    //    1ª vez: skeleton cheio | depois: só aviso na lista (input NÃO desmonta)
    useEffect(() => {
        if (!filter) return;
        (async () => {
            if (!initializedRef.current) {
                setLoading(true);
            } else {
                setListLoading(true);
            }
            setPage(1);
            await fetchClasses(1, true);
            setLoading(false);
            setListLoading(false);
            initializedRef.current = true;
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, debouncedBusca]);

    // 🔹 Regime de avaliação
    useEffect(() => {
        if (!filter) return;
        (async () => {
            const [q1, q2, q3, q4] = await Promise.all([
                getIstQuarter(filter, idSchool),
                getIIndQuarter(filter, idSchool),
                getIIIrdQuarter(filter, idSchool),
                getIVthQuarter(filter, idSchool),
            ].map(p => p.catch(() => null)));

            const primeiro = (r) => r?.data?.data?.find(x => x) || null;
            const i = primeiro(q1);
            setAssessmentRegime(i?.assessmentRegime);

            const total = i?.assessmentRegime?.toUpperCase() === "TRIMESTRAL" ? 3 : 4;
            setPeriodoAtual(periodoAtualPorData([i, primeiro(q2), primeiro(q3), primeiro(q4)], total));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const loadMore = useCallback(async () => {
        if (fetchingRef.current || !hasMore) return;
        const next = page + 1;
        setLoadingMore(true);
        setPage(next);
        await fetchClasses(next, false);
        setLoadingMore(false);
    }, [page, hasMore, fetchClasses]);

    // 🔹 Scroll infinito: observa o sentinela no fim da lista
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasMore || loading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { rootMargin: '300px' } // dispara um pouco antes de chegar ao fim
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loading, loadMore]);

    // verifica (em lote) quais turmas da lista já têm horário configurado
    useEffect(() => {
        const faltam = classes.map(c => String(c._id)).filter(id => !(id in comHorario));
        if (!faltam.length) return;
        let cancelled = false;
        getConfiguredSchedules(faltam)
            .then((r) => {
                if (cancelled) return;
                const ok = new Set(r?.data?.configurados || []);
                setComHorario(prev => {
                    const n = { ...prev };
                    faltam.forEach(id => { n[id] = ok.has(id); });
                    return n;
                });
            })
            .catch(() => { /* sem aviso se falhar */ });
        return () => { cancelled = true; };
    }, [classes, comHorario]);

    const NewClass = () => {
        navigate('/new/class');
    };

    const classInformation = (c) => {
        sessionStorage.setItem("ClassInformation", c._id);
        sessionStorage.setItem("serieClass", c.serie);
        navigate(`/class/info/${c._id}`);
    };

    return (
        <Container>
            {loading ? (
                <ContainerDivs>
                    <Search>
                        <FormSearch>
                            <label>Buscar Turma</label>
                            <AreaEmp><InputEmp type="text" placeholder="Buscar por nome ou código" disabled /></AreaEmp>
                        </FormSearch>
                        <FormFilter>
                            <label>Filtrar por Ano</label>
                            <Select disabled><option>{currentYear}</option></Select>
                        </FormFilter>
                    </Search>
                    <List>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <SkeletonRow key={i}>
                                <span className="square" />
                                <span className="bar" />
                                <span className="pill" />
                            </SkeletonRow>
                        ))}
                    </List>
                </ContainerDivs>
            ) : (
                <ContainerDivs>
                    <Search>
                        <FormSearch>
                            <label>Buscar Turma</label>
                            <AreaEmp>
                                <InputEmp
                                    type="text"
                                    placeholder="Buscar por nome ou código"
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                />
                            </AreaEmp>
                        </FormSearch>
                        <FormFilter>
                            <label>Filtrar por Ano</label>
                            <Select
                                id="position"
                                value={filter}
                                onChange={(e) => {
                                    setFilter(e.target.value);
                                    sessionStorage.setItem("classYearFilter", e.target.value);
                                }}
                            >
                                {years.map((y, index) => (
                                    <option key={index} value={y}>{y}</option>
                                ))}
                            </Select>
                        </FormFilter>
                    </Search>

                    <List>
                        {filter === currentYear && !positionAtEducationDepartment &&
                            <DivNewEmp>
                                <AddBtn onClick={NewClass}>Nova Turma</AddBtn>
                            </DivNewEmp>
                        }

                        <DiaryLegend>
                            <span><i className="blue" /> Em dia</span>
                            <span><i className="orange" /> Período anterior aberto</span>
                            <span><i className="red" /> 1º período ainda aberto no último</span>
                            <span><i className="green" /> Diários fechados</span>
                        </DiaryLegend>

                        <p>
                            Total de Turmas: <em>{total}</em>
                            {listLoading && " — atualizando..."}
                        </p>

                        {classes.length === 0 && !listLoading ? (
                            <EmptyState>
                                <span className="em">🔍</span>
                                Nenhuma turma encontrada.
                            </EmptyState>
                        ) : (
                            classes.map((c) => {
                                const diarios = c.dailyStatus || {};

                                const periodos = Object.keys(diarios).filter((periodo) => {
                                    if (assessmentRegime?.toUpperCase() === "TRIMESTRAL") {
                                        return periodo !== "4º BIMESTRE";
                                    }
                                    return true;
                                });

                                const isTrimestral = assessmentRegime?.toUpperCase() === "TRIMESTRAL";

                                // períodos com algum diário (regente ou Ed. Física) ainda aberto
                                const periodosAbertos = periodos
                                    .filter((periodo) =>
                                        Object.values(diarios[periodo] || {}).some(
                                            (status) => status !== "fechado"
                                        )
                                    )
                                    .sort((a, b) => parseInt(a) - parseInt(b));

                                const temDiarioAberto = periodosAbertos.length > 0;

                                const totalPeriodos = isTrimestral ? 3 : 4;
                                const numsAbertos = periodosAbertos.map(p => parseInt(p, 10)).filter(n => !isNaN(n));
                                const status = corDoDiario(numsAbertos, periodoAtual, totalPeriodos);

                                const nomeRegime = isTrimestral ? "trimestre" : "bimestre";
                                const descrever = (lista) =>
                                    `${juntarPeriodos(lista.map(p => p.match(/\d+º/)?.[0] || p))} ${nomeRegime}${lista.length > 1 ? "s" : ""}`;

                                // separa quem está com o diário aberto: Regente e/ou Ed. Física
                                const abertoPor = (chave) => periodosAbertos.filter((p) => {
                                    const st = (diarios[p] || {})[chave];
                                    return st !== undefined && st !== "fechado";
                                });
                                const linhasAbertas = [
                                    { quem: "Regente", lista: abertoPor("regentTeacher") },
                                    { quem: "Ed. Física", lista: abertoPor("physicalEducationTeacher") },
                                ]
                                    .filter(l => l.lista.length > 0)
                                    .map(l => `${l.quem}: ${descrever(l.lista)}`);

                                const textoPeriodos = linhasAbertas.join(" · ");

                                return (
                                    <Emp onClick={() => classInformation(c)} key={c._id}>
                                        <EmpMain>
                                            <ClassIcon>🏷️</ClassIcon>
                                            <NameWrap>
                                                <Span>{c.name}</Span>
                                                {c.classCode && (
                                                    <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted, #5b6b72)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        Cód. {c.classCode}
                                                    </span>
                                                )}
                                                {comHorario[String(c._id)] === false && String(c.year) === String(schoolYear) && (
                                                    <NoScheduleBadge
                                                        title="Turma sem grade horária — os professores lançam em qualquer dia letivo. Configure a grade horária na página da turma."
                                                        aria-label="Turma sem grade horária"
                                                    >
                                                        ⚠️ Sem grade horária
                                                    </NoScheduleBadge>
                                                )}
                                            </NameWrap>
                                        </EmpMain>
                                        <DiaryInfo>
                                            <DiaryBadge $open={temDiarioAberto} $status={status}>
                                                <span className="label">
                                                    {temDiarioAberto ? "Diário em aberto" : "Diários fechados"}
                                                </span>
                                            </DiaryBadge>
                                            {temDiarioAberto && (
                                                <DiaryPeriods $status={status} title={`Em aberto — ${textoPeriodos}`}>
                                                    {linhasAbertas.map((linha) => (
                                                        <span key={linha} style={{ display: "block" }}>{linha}</span>
                                                    ))}
                                                </DiaryPeriods>
                                            )}
                                        </DiaryInfo>
                                    </Emp>
                                );
                            })
                        )}

                        {/* sentinela do scroll infinito */}
                        <div ref={sentinelRef} style={{ height: 1 }} />

                        {loadingMore && (
                            <LoadMoreText>Carregando mais turmas...</LoadMoreText>
                        )}
                    </List>
                </ContainerDivs>
            )}
        </Container>
    );
};

export default Cla$$;